import { csvCell } from './csv'
import {
  CATEGORY_TYPICAL_W,
  POWER_CONSTANTS,
  PRESETS,
  classPf,
  formatVa,
  formatW,
  gensetCheck,
  inferPsuMode,
  nemaUnbalance,
  nextMcb,
  plnTier,
  poeChain,
  presetByKey,
  presetDemandW,
  resolveDraw,
  round,
  spread,
  vaToAmps,
  wallWatts,
  wattsToVa,
  type GensetVerdict,
  type InrushClass,
  type PfPreset,
  type PowerSource,
  type PsuMode,
  type ZeroReason,
} from './power'

/**
 * Builds a power plan from a saved snapshot. Pure — the page, the CSV and the
 * PDF all render these same rows, the same guarantee `run-sheet.ts` gives.
 *
 * The plan is a LOAD SCHEDULE, not an electrical design. Read the four
 * no-advice zones at the top of `power.ts` before extending this.
 */

/* ------------------------------------------------------------ the snapshot */

export type PowerPlanSettings = {
  voltage: number
  frequency: number
  /** Reserve on top of demand, as a percentage. Applied exactly once. */
  headroomPct: number
  /** 100 = IEC (no continuous-load derate); 80 = the NEC policy. */
  usablePct: number
  /** Fraction of full-white an LED wall actually runs at. */
  contentFraction: number
  /** Hours the show runs, for the battery/charger block. */
  showHours: number
  /** Size the plan is checked against, when there is one. */
  supply:
    | { kind: 'NONE' }
    | { kind: 'GENSET'; kva: number; rating?: 'PRIME' | 'STANDBY' }
    | { kind: 'PLN'; tierVa: number }
  /** Report at −10% nominal, where constant-power loads draw more current. */
  worstCaseVoltage: boolean
}

export type PowerPlanGroup = {
  id: string
  name: string
  /** The protective device this group sits behind, in amps. */
  breakerA: number | null
  /** 'L1' | 'L2' | 'L3', or null on a single-phase plan. */
  leg: string | null
  /** Simultaneity factor for the demand column only. Never the protection column. */
  diversity: number
  /** Fed through a UPS or rack PSU on this group. */
  upstream?: { efficiency: number; idleW: number } | null
}

export type PowerPlanItem = {
  id: string
  /** A catalogue/workspace device, a preset, or a free-text hand row. */
  kind: 'DEVICE' | 'PRESET' | 'MANUAL'
  deviceId?: string | null
  presetKey?: string | null
  label: string
  qty: number
  /** Which group(s) this row's cords land on. More than one only when redundant. */
  groupIds: string[]
  /** Overrides, all optional — a stated figure always wins. */
  wattsTypical?: number | null
  wattsMax?: number | null
  powerFactor?: number | null
  pfPreset?: PfPreset | null
  powerSource?: PowerSource | null
  /** For PoE/POC/BUS_POWERED rows: the row whose feed carries these watts. */
  hostItemId?: string | null
  inrush?: InrushClass | null
  note?: string | null
  /** LED wall etc.: how many m²/bays this row counts. */
  units?: number | null
  /**
   * The catalogue's maximum draw at the moment this row was added. Not used in
   * any arithmetic — it exists so the plan can TELL you the catalogue has moved
   * since, rather than silently changing a document somebody already emailed.
   */
  catalogueWattsAtSnapshot?: number | null
}

export type PowerPlanSnapshot = {
  schemaVersion: 1
  settings: PowerPlanSettings
  groups: PowerPlanGroup[]
  items: PowerPlanItem[]
}

/** What the builder needs to know about a catalogue device. */
export type PowerDeviceFacts = {
  id: string
  name: string
  manufacturer: string
  model: string
  category: string
  powerDrawTypicalW: number | null
  powerDrawMaxW: number | null
  powerSupplyRatedW: number | null
  powerSource: PowerSource
  powerDrawSource: string
  powerNote: string | null
  poePdDrawW: number | null
  poeType: number | null
  poeBudgetW: number | null
  mainsRatingA: number | null
  /** POWER INPUT ports, for the redundancy inference. */
  powerInlets: { connector: string; count?: number | null }[]
  /**
   * True when this workspace supplied the figure itself, because the catalogue
   * had none. The arithmetic does not care; the screen does, so a crew can see
   * whose number they are standing behind.
   */
  contributed?: boolean
}

/* --------------------------------------------------------------- the output */

export type PowerRow = {
  id: string
  label: string
  detail: string | null
  qty: number
  /** Catalogue category, or null on a free-text row. Sizes the gap warning. */
  category: string | null
  /** Null when nothing is published — the row enters no total and is named. */
  wattsTypical: number | null
  wattsMax: number | null
  /** After the DC-adapter term. */
  wallWattsMax: number | null
  powerFactor: number
  vaConnected: number | null
  vaDemand: number | null
  ampsConnected: number | null
  source: PowerSource
  psuMode: PsuMode
  psuCount: number
  outletsRequired: number
  groupIds: string[]
  inrush: InrushClass
  /** Set when the row contributes nothing to the mains total, and why. */
  zeroReason: ZeroReason | null
  /** Where its watts went instead, when they moved. */
  attributedTo: string | null
  provenance: string
  note: string | null
}

export type PowerGroupBlock = {
  id: string
  name: string
  breakerA: number | null
  leg: string | null
  rowCount: number
  psuCount: number
  outlets: number
  vaConnected: number
  /** With every redundant row at FULL VA, not its share. */
  vaFailover: number
  vaDemand: number
  ampsConnected: number
  ampsFailover: number
  usableA: number | null
  /** Failover when the group carries a redundant split, else connected. */
  bindingA: number
  utilisationPct: number | null
  over: boolean
  warnings: string[]
}

export type PowerCoverage = {
  totalRows: number
  known: number
  /** Rows whose figure came from a PSU nameplate rather than a draw spec. */
  derived: number
  unknown: number
  /** Rows with only one published figure, so demand equals connected. */
  noTypicalFigure: number
  /** The honest number: share of the plan's watts that rests on real figures. */
  knownWattsPct: number
  /**
   * Roughly what the rows with no figure could add, from class magnitudes.
   * Never added to any total — it exists only to say how wrong one might be.
   */
  unknownCouldAddW: number
}

export type PowerPlanTotals = {
  vaConnected: number
  vaDemand: number
  wDemand: number
  vaRequired: number
  wRequired: number
  ampsConnected: number
  ampsRequired: number
  aggregatePf: number | null
  genset: GensetVerdict | null
  pln: { requiredAmpsAtPlnVoltage: number; tier: { va: number; amps: number } | null; fits: boolean } | null
  suggestedMcb: number | null
}

export type PowerLegBlock = {
  leg: string
  vaConnected: number
  /** From this leg's own VA at line-to-neutral voltage, not the plan's amps / 3. */
  ampsConnected: number
  groupCount: number
}

export type BuiltPowerPlan = {
  rows: PowerRow[]
  groups: PowerGroupBlock[]
  unassigned: PowerRow[]
  totals: PowerPlanTotals
  coverage: PowerCoverage
  /** Rows deliberately excluded from the mains total, with the reason. */
  exclusions: { label: string; reason: ZeroReason; attributedTo: string | null }[]
  battery: { rows: number; wattHours: number } | null
  /**
   * Per-leg totals, and empty on a single-phase plan (no group names a leg).
   * A leg is what actually trips, so this is the number that binds on three
   * phase — never the plan total divided by three.
   */
  legs: PowerLegBlock[]
  /** Both imbalance conventions, or null under two live legs. */
  balance: { nemaPct: number; spreadPct: number } | null
  warnings: string[]
  assumptions: string[]
  /** Supply first, then groups ascending by PSU count then load. */
  powerUpOrder: { position: number; groupId: string; name: string; psuCount: number; note: string | null }[]
}

/* ------------------------------------------------------------------ helpers */

const ZERO_REASON_TEXT: Record<ZeroReason, string> = {
  INSIDE_HOST: 'installed inside another device on this plan',
  NONE_SOURCE: 'takes no power',
  BATTERY: 'battery powered — not a mains load',
  BUS_POWERED: 'powered by its host',
  POC: 'powered over its own cable by the transmitter',
  POE: 'powered over Ethernet',
  UPSTREAM_PSU: 'fed through a UPS on this plan',
  UNKNOWN_DRAW: 'no published power figure',
}

export const zeroReasonText = (reason: ZeroReason) => ZERO_REASON_TEXT[reason]

/**
 * One sentence about how far the answer can be trusted, written once so the
 * screen, the CSV and the PDF cannot disagree.
 *
 * `knownWattsPct` is a share of what could be modelled, so on a plan with
 * missing rows it happily reads 100% — sitting directly above "4 rows have no
 * figure", which turns a warning into reassurance. So the missing rows lead,
 * and the percentage is explicitly bounded to the rest.
 */
export function coverageSentence(coverage: PowerCoverage): string {
  const { totalRows, unknown, knownWattsPct } = coverage
  if (totalRows === 0) return 'Nothing on this plan yet.'
  const pct = Math.round(knownWattsPct)
  // The noun agrees with the total ("1 of 2 rows"), the verb with the count
  // ("1 ... has"). Pluralising both off the same number reads as broken English
  // on the one document a venue is going to read closely.
  const nounAll = totalRows === 1 ? 'row carries' : 'rows carry'
  if (unknown === 0) {
    return `All ${totalRows} ${nounAll} a figure, and ${pct}% of the watts come from a manufacturer-published number.`
  }
  const modelled = totalRows - unknown
  const noun = totalRows === 1 ? 'row' : 'rows'
  return (
    `${unknown} of ${totalRows} ${noun} ${unknown === 1 ? 'has' : 'have'} no figure at all and ` +
    `${unknown === 1 ? 'is' : 'are'} NOT in these totals. Of the ${modelled} that could be modelled, ` +
    `${pct}% of the watts come from a manufacturer-published number.`
  )
}

/** Sources that never place load at their own row. */
const ATTRIBUTED_AWAY: PowerSource[] = ['POE', 'POC', 'BUS_POWERED']

/* ------------------------------------------------------------------- build */

export function buildPowerPlan(
  snapshot: PowerPlanSnapshot,
  devices: Map<string, PowerDeviceFacts>,
): BuiltPowerPlan {
  const { settings, groups, items } = snapshot
  const voltage = settings.worstCaseVoltage ? POWER_CONSTANTS.worstCaseVoltage : settings.voltage
  const warnings: string[] = []
  const assumptions: string[] = []

  // Watts routed onto a host row by PoE, PoC or a bus, keyed by host ITEM id.
  const attributedW = new Map<string, number>()
  const poeDemand = new Map<string, number>()
  // Item id -> its device facts. The device map is keyed by device id, so
  // pass 2 needs this to find a host's PoE budget from its row.
  const deviceForItem = new Map<string, PowerDeviceFacts>()
  for (const item of items) {
    const facts = item.deviceId ? devices.get(item.deviceId) : undefined
    if (facts) deviceForItem.set(item.id, facts)
  }

  /* -- pass 1: resolve every row's own figures ---------------------------- */
  const rows: PowerRow[] = items.map((item) => {
    const device = item.deviceId ? devices.get(item.deviceId) : undefined
    const preset = item.presetKey ? presetByKey(item.presetKey) : undefined
    const units = item.units ?? 1

    let typicalW: number | null = null
    let maxW: number | null = null
    let provenance = 'MANUAL'
    let detail: string | null = null
    let note = item.note ?? null
    let inrush: InrushClass = item.inrush ?? 'PSU'
    let source: PowerSource = item.powerSource ?? 'UNKNOWN'
    let inlets: { connector: string; count?: number | null }[] = []

    if (device) {
      const draw = resolveDraw(device)
      typicalW = item.wattsTypical ?? draw.typicalW
      maxW = item.wattsMax ?? draw.maxW
      provenance = device.powerDrawSource
      source = item.powerSource ?? device.powerSource
      // The label is already "<make> <model>", so repeating it here would fill
      // the row's second line with nothing. What is not on the label is what
      // kind of box this is.
      detail = device.name
      note = note ?? device.powerNote
      inlets = device.powerInlets
      if (item.inrush == null) inrush = 'PSU'
    } else if (preset) {
      maxW = (item.wattsMax ?? preset.connectedW) * units
      typicalW = (item.wattsTypical ?? presetDemandW(preset, settings)) * units
      provenance = 'PRESET'
      source = item.powerSource ?? preset.source
      inrush = item.inrush ?? preset.inrush
      detail = preset.unit ? `${units} ${preset.unit}` : null
      if (preset.key === 'led-wall') {
        assumptions.push(
          `LED wall modelled at ${Math.round(settings.contentFraction * 100)}% of full-white peak. This one figure moves the total more than the whole video rack.`,
        )
      }
    } else {
      typicalW = item.wattsTypical ?? item.wattsMax ?? null
      maxW = item.wattsMax ?? item.wattsTypical ?? null
      source = item.powerSource ?? 'MAINS_AC'
    }

    const { mode, psuCount } = inferPsuMode(inlets)
    const wallMax = maxW == null ? null : wallWatts(maxW, source)
    const wallTyp = typicalW == null ? null : wallWatts(typicalW, source)
    const pf = classPf({
      powerFactor: item.powerFactor,
      pfPreset: item.pfPreset,
      powerSource: source,
      wallW: wallMax,
    })

    // Attribution — decided here, summed in pass 2.
    let zeroReason: ZeroReason | null = null
    let attributedTo: string | null = null

    if (source === 'NONE') zeroReason = 'NONE_SOURCE'
    else if (source === 'BATTERY') zeroReason = 'BATTERY'
    else if (ATTRIBUTED_AWAY.includes(source)) {
      zeroReason = source === 'POE' ? 'POE' : source === 'POC' ? 'POC' : 'BUS_POWERED'
      const host = item.hostItemId ? items.find((i) => i.id === item.hostItemId) : undefined
      attributedTo = host ? host.label : 'host not on this plan'
      if (host && source === 'POE') {
        const pdW = (device?.poePdDrawW ?? maxW ?? 0) * item.qty
        if (pdW > 0) {
          const { pseW, hostAcW } = poeChain(pdW, device?.poeType ?? null)
          attributedW.set(host.id, (attributedW.get(host.id) ?? 0) + hostAcW)
          poeDemand.set(host.id, (poeDemand.get(host.id) ?? 0) + pseW)
        }
      } else if (host && source === 'POC' && maxW != null) {
        // A PoC transmitter's own published figure normally already includes
        // its receivers, so nothing is added — the receiver row simply reads 0.
        attributedTo = host.label
      }
    } else if (wallMax == null) {
      zeroReason = 'UNKNOWN_DRAW'
    }

    const category = device?.category ?? null
    const vaConnected = zeroReason || wallMax == null ? null : wattsToVa(wallMax, pf) * item.qty
    const vaDemand = zeroReason || wallTyp == null ? null : wattsToVa(wallTyp, pf) * item.qty

    return {
      id: item.id,
      label: item.label,
      detail,
      qty: item.qty,
      category,
      wattsTypical: typicalW,
      wattsMax: maxW,
      wallWattsMax: wallMax,
      powerFactor: pf,
      vaConnected,
      vaDemand,
      ampsConnected: vaConnected == null ? null : vaToAmps(vaConnected, voltage),
      source,
      psuMode: mode,
      psuCount,
      outletsRequired: item.qty * (mode === 'REDUNDANT' ? psuCount : 1),
      groupIds: item.groupIds,
      inrush,
      zeroReason,
      attributedTo,
      provenance,
      note,
    }
  })

  /* -- pass 2: fold attributed watts onto their hosts --------------------- */
  const byId = new Map(rows.map((r) => [r.id, r]))
  for (const [hostId, extraW] of attributedW) {
    const host = byId.get(hostId)
    if (!host) continue
    const base = host.wallWattsMax ?? 0
    const combined = base + extraW
    host.wallWattsMax = combined
    host.powerFactor = classPf({ powerSource: host.source, wallW: combined })
    host.vaConnected = wattsToVa(combined, host.powerFactor) * 1
    host.vaDemand = wattsToVa(combined, host.powerFactor) * 1
    host.ampsConnected = vaToAmps(host.vaConnected, voltage)
    host.zeroReason = null
    host.note = [host.note, `Includes ${Math.round(extraW)} W of PoE load from its powered devices.`]
      .filter(Boolean)
      .join(' ')
    const budget = deviceForItem.get(hostId)?.poeBudgetW
    const demanded = poeDemand.get(hostId) ?? 0
    if (budget && demanded > budget) {
      warnings.push(
        `${host.label}: PoE devices ask ${Math.round(demanded)} W at the ports but the switch budget is ${budget} W.`,
      )
    }
  }

  /* -- pass 3: per group -------------------------------------------------- */
  const groupBlocks: PowerGroupBlock[] = groups.map((group) => {
    const members = rows.filter((r) => r.groupIds.includes(group.id) && !r.zeroReason)
    let vaConnected = 0
    let vaFailover = 0
    let vaDemand = 0
    let outlets = 0
    let psuCount = 0
    const groupWarnings: string[] = []

    for (const row of members) {
      const cords = row.psuMode === 'REDUNDANT' ? row.groupIds.length : 1
      const share = cords > 0 ? cords : 1
      // Consumption is counted ONCE across the feeds it is split over; each
      // feed must nonetheless be sized for the whole of it.
      vaConnected += (row.vaConnected ?? 0) / share
      vaFailover += row.psuMode === 'REDUNDANT' ? (row.vaConnected ?? 0) : (row.vaConnected ?? 0) / share
      vaDemand += (row.vaDemand ?? 0) / share
      outlets += row.outletsRequired / (row.groupIds.length || 1)
      psuCount += row.psuCount * row.qty
      if (row.psuMode === 'REDUNDANT' && row.groupIds.length < 2) {
        groupWarnings.push(
          `${row.label} has ${row.psuCount} supplies but both cords land on this one group — the redundancy is defeated.`,
        )
      }
    }

    let upstreamNote: string | null = null
    if (group.upstream) {
      const before = vaConnected
      vaConnected = vaConnected / group.upstream.efficiency + group.upstream.idleW
      vaFailover = vaFailover / group.upstream.efficiency + group.upstream.idleW
      vaDemand = vaDemand / group.upstream.efficiency + group.upstream.idleW
      upstreamNote = `Through a UPS at ${Math.round(group.upstream.efficiency * 100)}% plus ${group.upstream.idleW} W idle: ${formatVa(before)} becomes ${formatVa(vaConnected)}.`
      groupWarnings.push(upstreamNote)
    }

    vaDemand *= group.diversity

    const ampsConnected = vaToAmps(vaConnected, voltage)
    const ampsFailover = vaToAmps(vaFailover, voltage)
    const hasRedundantSplit = members.some((r) => r.psuMode === 'REDUNDANT' && r.groupIds.length > 1)
    const bindingA = hasRedundantSplit ? ampsFailover : ampsConnected
    const usableA = group.breakerA == null ? null : (group.breakerA * settings.usablePct) / 100
    const utilisationPct = usableA == null ? null : (bindingA / usableA) * 100

    if (usableA != null && bindingA > usableA) {
      groupWarnings.push(
        hasRedundantSplit
          ? `Over the ${group.breakerA} A device on the far feed's failure: ${round(ampsFailover, 1)} A against ${round(usableA, 1)} A usable.`
          : `Over the ${group.breakerA} A device: ${round(bindingA, 1)} A against ${round(usableA, 1)} A usable.`,
      )
    }

    return {
      id: group.id,
      name: group.name,
      breakerA: group.breakerA,
      leg: group.leg,
      rowCount: members.length,
      psuCount,
      outlets: Math.ceil(outlets),
      vaConnected,
      vaFailover,
      vaDemand,
      ampsConnected,
      ampsFailover,
      usableA,
      bindingA,
      utilisationPct,
      over: usableA != null && bindingA > usableA,
      warnings: groupWarnings,
    }
  })

  /* -- pass 4: plan totals ------------------------------------------------ */
  const unassigned = rows.filter((r) => !r.zeroReason && r.groupIds.length === 0)
  const unassignedVaConnected = unassigned.reduce((a, r) => a + (r.vaConnected ?? 0), 0)
  const unassignedVaDemand = unassigned.reduce((a, r) => a + (r.vaDemand ?? 0), 0)

  const vaConnected = groupBlocks.reduce((a, g) => a + g.vaConnected, 0) + unassignedVaConnected
  const vaDemand = groupBlocks.reduce((a, g) => a + g.vaDemand, 0) + unassignedVaDemand
  const wDemand = rows.reduce((a, r) => {
    if (r.zeroReason || r.vaDemand == null) return a
    return a + r.vaDemand * r.powerFactor
  }, 0)

  const headroom = 1 + settings.headroomPct / 100
  const vaRequired = vaDemand * headroom
  const wRequired = wDemand * headroom

  let genset: GensetVerdict | null = null
  let pln: PowerPlanTotals['pln'] = null
  if (settings.supply.kind === 'GENSET') {
    genset = gensetCheck(vaRequired, wRequired, settings.supply.kva)
    const aggregate = vaDemand > 0 ? wDemand / vaDemand : 0
    if (aggregate > 0.95) {
      warnings.push(
        'Aggregate power factor is above 0.95. A modern PFC rack sits near the leading edge of an alternator’s 0.8 lagging to 0.9 leading window — raise it with your generator supplier.',
      )
    }
    if (settings.supply.rating === 'STANDBY') {
      assumptions.push(
        'The set is rated STANDBY. A standby rating is not a prime rating for an all-day show (ISO 8528-1 defines prime around a 70% 24-hour average load factor).',
      )
    }
  } else if (settings.supply.kind === 'PLN') {
    const t = plnTier(vaRequired)
    pln = { ...t, fits: settings.supply.tierVa >= vaRequired }
    if (!pln.fits) {
      warnings.push(
        `Over the ${settings.supply.tierVa} VA tier: the plan needs ${formatVa(vaRequired)}${t.tier ? `, which is the ${t.tier.va} VA tier (1 x ${t.tier.amps} A)` : ''}.`,
      )
    }
  }

  /* -- coverage, exclusions, battery, power-up ---------------------------- */
  const mainsRows = rows.filter((r) => !r.zeroReason || r.zeroReason === 'UNKNOWN_DRAW')
  const known = mainsRows.filter((r) => r.vaConnected != null && r.provenance === 'PUBLISHED').length
  const derived = mainsRows.filter((r) => r.provenance === 'DERIVED_FROM_PSU').length
  const unknownRows = rows.filter((r) => r.zeroReason === 'UNKNOWN_DRAW')
  const knownWatts = rows.reduce(
    (a, r) => (r.provenance === 'PUBLISHED' && r.vaConnected != null ? a + r.vaConnected : a),
    0,
  )

  const coverage: PowerCoverage = {
    totalRows: rows.length,
    known,
    derived,
    unknown: unknownRows.length,
    noTypicalFigure: rows.filter((r) => r.wattsMax != null && r.wattsTypical === r.wattsMax).length,
    knownWattsPct: vaConnected > 0 ? (knownWatts / vaConnected) * 100 : 0,
    unknownCouldAddW: unknownRows.reduce(
      (a, r) => a + (CATEGORY_TYPICAL_W[r.category ?? 'OTHER'] ?? CATEGORY_TYPICAL_W.OTHER ?? 100) * r.qty,
      0,
    ),
  }

  if (unknownRows.length > 0) {
    warnings.push(
      `${unknownRows.length} row${unknownRows.length === 1 ? '' : 's'} have no published figure and are in no total: ${unknownRows
        .slice(0, 4)
        .map((r) => r.label)
        .join(', ')}${unknownRows.length > 4 ? '…' : ''}.`,
    )
  }
  if (coverage.noTypicalFigure > 0) {
    assumptions.push(
      `${coverage.noTypicalFigure} row(s) publish a single figure, so their demand equals their connected load. The demand column is optimistic by that much.`,
    )
  }

  const batteryRows = rows.filter((r) => r.zeroReason === 'BATTERY')
  const battery =
    batteryRows.length === 0
      ? null
      : {
          rows: batteryRows.length,
          wattHours: batteryRows.reduce(
            (a, r) => a + (r.wattsTypical ?? r.wattsMax ?? 0) * r.qty * settings.showHours,
            0,
          ),
        }

  const powerUpOrder = [...groupBlocks]
    .sort((a, b) => a.psuCount - b.psuCount || a.vaConnected - b.vaConnected)
    .map((g, index) => ({
      position: index + 1,
      groupId: g.id,
      name: g.name,
      psuCount: g.psuCount,
      note:
        g.psuCount >= 10
          ? `${g.psuCount} separate supplies — stage it last and confirm the breaker curve with the venue.`
          : null,
    }))

  assumptions.push(
    `${voltage} V ${settings.frequency} Hz${settings.worstCaseVoltage ? ' (worst case, −10% of nominal)' : ''}.`,
    settings.usablePct === 100
      ? 'No continuous-load derate: PUIL 2011 / IEC 60364 sizes to Ib ≤ In ≤ Iz.'
      : `Breakers derated to ${settings.usablePct}% (a policy, not Indonesian code).`,
    `Headroom ${settings.headroomPct}% applied once, to the demand figure.`,
  )

  /* -- per leg ------------------------------------------------------------ */
  // Balance is a statement about normal running, so it is built from each
  // group's connected VA. The failover figure is a fault case and belongs to
  // the group that carries it, not to a leg's everyday loading.
  const byLeg = new Map<string, { va: number; groups: number }>()
  for (const group of groupBlocks) {
    if (!group.leg) continue
    const entry = byLeg.get(group.leg) ?? { va: 0, groups: 0 }
    entry.va += group.vaConnected
    entry.groups += 1
    byLeg.set(group.leg, entry)
  }
  const legs: PowerLegBlock[] = [...byLeg]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([leg, entry]) => ({
      leg,
      vaConnected: entry.va,
      ampsConnected: vaToAmps(entry.va, voltage),
      groupCount: entry.groups,
    }))
  // Two conventions because they disagree and people quote both: NEMA measures
  // against the mean, the spread against the largest leg.
  const balance =
    legs.length >= 2
      ? {
          nemaPct: nemaUnbalance(legs.map((l) => l.vaConnected)),
          spreadPct: spread(legs.map((l) => l.vaConnected)),
        }
      : null

  return {
    rows,
    groups: groupBlocks,
    unassigned,
    totals: {
      vaConnected,
      vaDemand,
      wDemand,
      vaRequired,
      wRequired,
      ampsConnected: vaToAmps(vaConnected, voltage),
      ampsRequired: vaToAmps(vaRequired, voltage),
      aggregatePf: vaDemand > 0 ? wDemand / vaDemand : null,
      genset,
      pln,
      suggestedMcb: nextMcb(vaToAmps(vaRequired, voltage)),
    },
    coverage,
    exclusions: rows
      .filter((r) => r.zeroReason)
      .map((r) => ({ label: r.label, reason: r.zeroReason as ZeroReason, attributedTo: r.attributedTo })),
    battery,
    legs,
    balance,
    warnings,
    assumptions,
    powerUpOrder,
  }
}

/* --------------------------------------------------------------------- CSV */

/**
 * The schedule as a spreadsheet: rows, group totals, plan totals, coverage,
 * warnings, assumptions and the power-up order — everything the PDF shows,
 * because a spreadsheet that omits the caveats is the one that gets forwarded.
 *
 * UTF-8 BOM and CRLF, matching the run sheet, so Excel opens it directly.
 */
export function powerPlanToCsv(plan: BuiltPowerPlan, name: string, generatedAt: string): string {
  return '\ufeff' + powerPlanCsvRows(plan, name, generatedAt).map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n'
}

/** The rows behind the CSV, exposed for tests. */
export function powerPlanCsvRows(plan: BuiltPowerPlan, name: string, generatedAt: string): string[][] {
  const rows: string[][] = [
    [`Power plan — ${name}`],
    [`Generated ${generatedAt}`],
    ['This is a load schedule for an electrician to check, not an electrical design.'],
    ['Breaker and cable sizing remain a licensed electrician’s call under the local authority.'],
    [],
    ['Group', 'Item', 'Qty', 'Typical W', 'Max W', 'PF', 'VA (connected)', 'A', 'Source', 'Outlets', 'Provenance', 'Note'],
  ]

  const groupName = new Map(plan.groups.map((g) => [g.id, g.name]))
  for (const row of plan.rows) {
    rows.push([
      row.groupIds.map((id) => groupName.get(id) ?? id).join(' + ') || 'Unassigned',
      row.label,
      String(row.qty),
      row.wattsTypical == null ? '' : String(round(row.wattsTypical, 1)),
      row.wattsMax == null ? '' : String(round(row.wattsMax, 1)),
      String(row.powerFactor),
      row.vaConnected == null ? '' : String(round(row.vaConnected, 1)),
      row.ampsConnected == null ? '' : String(round(row.ampsConnected, 2)),
      row.source,
      String(row.outletsRequired),
      row.provenance,
      row.zeroReason ? `Not counted: ${zeroReasonText(row.zeroReason)}${row.attributedTo ? ` (${row.attributedTo})` : ''}` : (row.note ?? ''),
    ])
  }

  rows.push([], ['Group totals'], ['Group', 'Breaker A', 'Connected VA', 'On failover VA', 'Demand VA', 'A', 'Usable A', 'Utilisation %', 'Outlets', 'PSUs'])
  for (const g of plan.groups) {
    rows.push([
      g.name,
      g.breakerA == null ? '' : String(g.breakerA),
      String(round(g.vaConnected, 1)),
      String(round(g.vaFailover, 1)),
      String(round(g.vaDemand, 1)),
      String(round(g.bindingA, 2)),
      g.usableA == null ? '' : String(round(g.usableA, 1)),
      g.utilisationPct == null ? '' : String(round(g.utilisationPct, 1)),
      String(g.outlets),
      String(g.psuCount),
    ])
  }

  const t = plan.totals
  rows.push(
    [],
    ['Plan totals'],
    ['Connected (sizes distro and breakers)', formatVa(t.vaConnected), `${round(t.ampsConnected, 2)} A`],
    ['Demand (sizes the genset and fuel)', formatVa(t.vaDemand), formatW(t.wDemand)],
    ['Required with headroom', formatVa(t.vaRequired), formatW(t.wRequired)],
  )
  if (t.genset) {
    rows.push([
      'Genset',
      `${round(t.genset.kvaUtil, 1)}% of kVA`,
      `${round(t.genset.kwUtil, 1)}% of kW`,
      `binds on ${t.genset.binds}`,
      t.genset.note ?? 'within band',
    ])
  }
  if (t.pln) {
    rows.push(['PLN tier', t.pln.fits ? 'fits' : 'OVER', t.pln.tier ? `${t.pln.tier.va} VA (1 x ${t.pln.tier.amps} A)` : 'above the published tiers'])
  }

  rows.push(
    [],
    ['Coverage'],
    ['Rows with a published figure', `${plan.coverage.known} of ${plan.coverage.totalRows}`],
    ['Derived from a PSU rating', String(plan.coverage.derived)],
    ['No figure — in no total', String(plan.coverage.unknown)],
    ['Share of the plan’s VA resting on published figures', `${round(plan.coverage.knownWattsPct, 1)}%`],
    // The same sentence the screen and the PDF carry, so a reader who only
    // ever sees the spreadsheet gets the caveat too.
    ['In words', coverageSentence(plan.coverage)],
  )

  if (plan.warnings.length > 0) {
    rows.push([], ['Warnings'], ...plan.warnings.map((w) => [w]))
  }
  rows.push([], ['Assumptions'], ...plan.assumptions.map((a) => [a]))
  rows.push(
    [],
    ['Power-up order'],
    ...plan.powerUpOrder.map((p) => [String(p.position), p.name, `${p.psuCount} PSUs`, p.note ?? '']),
  )
  return rows
}

export { PRESETS }
